import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { APP_PROTOCOL, formatMoney, type OrderDTO } from '@va/shared';
import { api } from '../api/client';
import { IconCheck } from '../components/icons';
import { VAMark } from '../components/Brand';
import { ErrorBox, Spinner } from '../components/ui';
import { desktop, isDesktop } from '../platform';
import { useAuth } from '../state/auth';
import { useCart } from '../state/cart';

/**
 * The browser half of a purchase started in the desktop app. That browser usually isn't signed in
 * to the store, and doesn't need to be: the app sees the payment and unlocks it by itself.
 */
function ReturnToApp({ title, link, children }: { title: string; link: string; children: ReactNode }) {
  return (
    <div className="container auth-wrap">
      <div className="card auth-card center">
        <VAMark className="auth-mark" />
        <h2>{title}</h2>
        <p className="muted">{children}</p>
        <a className="btn btn-primary" href={link}>Return to app</a>
        <p className="small dim" style={{ marginTop: 14 }}>You can close this tab.</p>
      </div>
    </div>
  );
}

export function CheckoutCompletePage() {
  const [params] = useSearchParams();
  const orderId = params.get('order');
  const fromApp = !isDesktop && params.get('app') === '1';
  const { user, ready } = useAuth();
  const cart = useCart();
  const qc = useQueryClient();
  const started = useRef(Date.now());
  const synced = useRef(false);

  const order = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => api.get<OrderDTO>(`/orders/${orderId}`),
    enabled: !!orderId && !!user && !fromApp,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d && d.status !== 'pending') return false;
      return Date.now() - started.current < 10 * 60_000 ? 2000 : false;
    },
    // Keep checking while the customer pays in the browser or watches Resolume: the app is
    // behind those windows, and the license should land the moment the payment does.
    refetchIntervalInBackground: true,
  });
  const o = order.data;

  // In the app the payment runs in a window of its own (desktop checkout.ts): follow it.
  const [payWindow, setPayWindow] = useState<'open' | 'closed' | 'browser'>('open');
  const inAppPayment = !!desktop?.checkout && !!params.get('waiting');
  useEffect(() => {
    if (!desktop?.checkout || !orderId) return;
    return desktop.checkout.onEvent((e) => {
      if (e.orderId !== orderId) return;
      if (e.outcome === 'cancel') {
        // Back from the payment page without paying.
        void api
          .post(`/orders/${orderId}/cancel`)
          .catch(() => {})
          .finally(() => void order.refetch());
      } else if (e.outcome === 'complete') {
        void order.refetch();
      } else {
        setPayWindow(e.outcome);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);
  const payInBrowser = () => {
    setPayWindow('browser');
    void desktop?.checkout.inBrowser();
  };

  useEffect(() => {
    if (o?.status !== 'paid' || synced.current) return;
    synced.current = true;
    o.items.forEach((i) => cart.remove(i.productId));
    void (async () => {
      if (desktop) await desktop.license.sync();
      await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'order' });
    })();
  }, [o, cart, qc]);

  if (!orderId) return <div className="container"><ErrorBox error={new Error('Missing order.')} /></div>;
  if (fromApp) {
    const link = `${APP_PROTOCOL}://checkout/complete?order=${encodeURIComponent(orderId)}`;
    return params.get('error') || params.get('status') ? (
      <ReturnToApp title="Check your order in the app" link={link}>
        We couldn't confirm the payment here. The VA Plugin Store app shows your order's status.
      </ReturnToApp>
    ) : (
      <ReturnToApp title="Payment received" link={link}>
        Thank you! Your purchase unlocks in the VA Plugin Store app within a few seconds, plugins you already installed included.
      </ReturnToApp>
    );
  }
  if (ready && !user) {
    return (
      <div className="container auth-wrap">
        <div className="card center auth-card">
          <h2>Payment received?</h2>
          <p className="muted">Sign in to see your order and unlock your products.</p>
          <Link className="btn btn-primary" to="/login" state={{ from: `/checkout/complete?order=${orderId}` }}>Sign in</Link>
        </div>
      </div>
    );
  }

  const waiting = !o || o.status === 'pending';
  return (
    <div className="container auth-wrap">
      <div className="card auth-card center" style={{ width: 'min(560px, 100%)' }}>
        <VAMark className="auth-mark" />
        {order.error && <ErrorBox error={order.error} onRetry={() => order.refetch()} />}
        {waiting && !order.error && (
          <>
            <h2>Waiting for your payment…</h2>
            <p className="muted">
              {inAppPayment && payWindow === 'open'
                ? 'Finish paying in the payment window. This page updates by itself.'
                : inAppPayment && payWindow === 'closed'
                  ? 'The payment window was closed before the payment went through.'
                  : isDesktop || params.get('waiting')
                    ? 'Finish paying in your browser. This page updates by itself.'
                    : 'Confirming with the payment provider. This usually takes a few seconds.'}
            </p>
            <Spinner />
            {inAppPayment && payWindow === 'closed' && (
              <div className="row wrap" style={{ justifyContent: 'center', gap: 8, marginTop: 14 }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setPayWindow('open');
                    void desktop?.checkout.reopen();
                  }}
                >
                  Open the payment window
                </button>
                <button className="btn btn-ghost btn-sm" onClick={payInBrowser}>Pay in your browser</button>
              </div>
            )}
            {inAppPayment && payWindow === 'open' && (
              <p className="small dim" style={{ marginTop: 18 }}>
                Trouble with the payment window? <button className="link-btn" onClick={payInBrowser}>Pay in your browser instead</button>.
              </p>
            )}
            <p className="small dim" style={{ marginTop: 18 }}>
              Closed the payment page by mistake? <Link to="/cart">Go back to your cart</Link>.
            </p>
          </>
        )}
        {o?.status === 'paid' && (
          <>
            <span className="pill success" style={{ marginBottom: 12 }}><IconCheck size={14} /> Paid · {o.number}</span>
            <h2>You're all set</h2>
            <p className="muted">
              {o.items.map((i) => i.name).join(', ')} {o.items.length === 1 ? 'is' : 'are'} now in My Library. A receipt is on its way to {o.email}.
            </p>
            {o.totalCents > 0 && <p className="mono" style={{ color: 'var(--va-headline)' }}>{formatMoney(o.totalCents, o.currency)}</p>}
            {isDesktop ? (
              <p className="small muted">Your licenses are already updated on this computer: installed plugins drop the watermark within a few seconds.</p>
            ) : (
              <p className="small muted">Open the VA Plugin Store app and sign in. Installed plugins drop the watermark within a few seconds.</p>
            )}
            <div className="row wrap" style={{ justifyContent: 'center', marginTop: 10 }}>
              <Link className="btn btn-primary" to="/library">Go to My Library</Link>
              <Link className="btn btn-outline" to="/">Keep browsing</Link>
            </div>
          </>
        )}
        {o && (o.status === 'failed' || o.status === 'canceled') && (
          <>
            <h2>Payment not completed</h2>
            <p className="muted">Nothing was charged. Your cart is still here if you want to try again.</p>
            <Link className="btn btn-primary" to="/cart">Back to cart</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function CheckoutCancelPage() {
  const [params] = useSearchParams();
  if (!isDesktop && params.get('app') === '1') {
    return (
      <ReturnToApp title="Checkout canceled" link={`${APP_PROTOCOL}://cart`}>
        No payment was taken. Your cart is still in the VA Plugin Store app.
      </ReturnToApp>
    );
  }
  return (
    <div className="container auth-wrap">
      <div className="card auth-card center">
        <VAMark className="auth-mark" />
        <h2>Checkout canceled</h2>
        <p className="muted">No payment was taken. Your cart is saved.</p>
        <Link className="btn btn-primary" to="/cart">Back to cart</Link>
      </div>
    </div>
  );
}
